import { Troubleshoot } from "@mui/icons-material";
import type { FC } from "react";
import { useTranslation } from "react-i18next";
import useEvent from "react-use-event-hook";
import { useRecoilState } from "recoil";

import { AccessControl, FramerTooltip } from "@ryde/controls";
import {
  ActionsEnum,
  rideInvestigationRideIdAtom,
  TOOLTIP_DELAY_BUTTONS,
  type IRideDto,
} from "@ryde/shared";

import { ButtonHeader } from "../../../../right";

interface IRideInvestigateButtonProps {
  rideDto: Partial<IRideDto>;
}

/**
 * Opens Deep Investigation for the ride being edited. It only writes the ride id — the overlay itself is
 * mounted once at the app root (`InvestigationOverlayHost` in `@ryde/pages`), which is why this button can
 * live in layout at all: layout must not import pages, so the two meet through the shared atom.
 *
 * The overlay opens ON TOP of this drawer, leaving it open underneath. Clicking again closes it, and opening
 * while another ride is under investigation retargets the overlay to this one. A ride without GPS opens too:
 * the investigation shows its history and stations and says there is no drive to replay.
 */
export const RideInvestigateButton: FC<IRideInvestigateButtonProps> = ({
  rideDto,
}) => {
  const { t } = useTranslation();
  const [currentRideId, setRideId] = useRecoilState(
    rideInvestigationRideIdAtom,
  );

  const rideId = rideDto.id;
  const isActive = currentRideId !== undefined && currentRideId === rideId;

  const handleClick = useEvent(() => {
    setRideId(isActive ? undefined : rideId);
  });

  // Hidden rather than disabled while creating or duplicating: the investigation endpoint needs a persisted
  // ride, and a permanently dead icon in that mode reads as a bug.
  if (!rideId) {
    return null;
  }

  return (
    <AccessControl entity="ride" action={ActionsEnum.Read}>
      <FramerTooltip
        content={t("rideMap.investigation.buttonTooltip")}
        mouseEnterDelay={TOOLTIP_DELAY_BUTTONS}
      >
        <span>
          <ButtonHeader
            $active={isActive}
            onClick={handleClick}
            data-e2e="ride-investigation-trigger-drawer"
          >
            <Troubleshoot />
          </ButtonHeader>
        </span>
      </FramerTooltip>
    </AccessControl>
  );
};
